import type { SignalSurfaces, SignalValues } from './conciergeState'
import {
  CITY_LOOKUP,
  INTEREST_LOOKUP,
  INTEREST_NAMES,
  MAX_NGRAM,
  OCCASION_LOOKUP,
  RELATIONSHIP_LOOKUP,
  matchBudget,
  normalizeKey,
} from './signalDictionary'

export type ParsedSignals = {
  signals: Partial<SignalValues>
  // The words the buyer actually used for each signal above, when they differ from
  // the canonical it resolved to — "husband" for spouse. A sibling map rather than
  // pairs inside `signals` so every existing `parsed.signals.x` read still works.
  surfaces: SignalSurfaces
  // Tokens no dictionary claimed. Kept, not dropped: they're the raw material
  // for a future meaning-match path and for spotting words worth adding.
  unmatched: string[]
}

export type SignalParser = (raw: string) => Promise<ParsedSignals>

// Programmatic keyword matcher — no AI. Tokenizes the buyer's text, walks it
// left-to-right taking the longest dictionary phrase at each position, and reads
// a budget straight out of the string. A production parser (a fuller word list,
// then an LLM) swaps in behind the same SignalParser interface without touching
// the state model or the input UI.
export const parseSignals: SignalParser = async raw => matchSignals(raw)

export function matchSignals (raw: string): ParsedSignals {
  const tokens = tokenize(raw)
  const consumed = new Array<boolean>(tokens.length).fill(false)

  let relationship: string | undefined
  let occasion: string | undefined
  let location: string | undefined
  const interests: string[] = []
  const surfaces: SignalSurfaces = {}

  // Only worth keeping when it says something the canonical doesn't. A city
  // matches its own name, so recording the tokenized "seattle" would just lose
  // the capitalisation the dictionary already has.
  function noteSurface (name: keyof SignalSurfaces, canonical: string, phrase: string) {
    if (normalizeKey(canonical) !== phrase) surfaces[name] = phrase
  }

  for (let i = 0; i < tokens.length;) {
    const match = longestPhraseAt(tokens, i)
    if (!match) { i += 1; continue }

    const { canonical, category, length } = match
    const phrase = tokens.slice(i, i + length).join(' ')

    if (category === 'relationship') {
      if (relationship === undefined) noteSurface('relationship', canonical, phrase)
      relationship ??= canonical
    }
    else if (category === 'occasion') {
      if (occasion === undefined) noteSurface('occasion', canonical, phrase)
      occasion ??= canonical
    }
    else if (category === 'location') {
      if (location === undefined) noteSurface('location', canonical, phrase)
      location ??= canonical
    }
    else if (!interests.includes(canonical)) { interests.push(canonical) }

    for (let j = i; j < i + length; j++) consumed[j] = true
    i += length
  }

  // Fuzzy interest pass over the leftover single tokens, so exact multi-word
  // phrases always win first.
  tokens.forEach((token, i) => {
    if (consumed[i]) return
    const canonical = fuzzyInterest(token)
    if (canonical && !interests.includes(canonical)) {
      interests.push(canonical)
      consumed[i] = true
    }
  })

  const budget = matchBudget(raw)

  const signals: Partial<SignalValues> = {}
  if (relationship) signals.relationship = relationship
  if (occasion) signals.occasion = occasion
  if (location) signals.location = location
  if (interests.length) signals.interests = interests
  if (budget) signals.budget = budget.range

  const unmatched = tokens.filter((_, i) => !consumed[i])
  return { signals, surfaces, unmatched }
}

type PhraseCategory = 'relationship' | 'occasion' | 'location' | 'interest'
type PhraseMatch = { canonical: string, category: PhraseCategory, length: number }

// Longest phrase at position i wins, and among equal lengths the categories are
// tried highest-stakes first so a shared word resolves predictably.
function longestPhraseAt (tokens: string[], i: number): PhraseMatch | null {
  const maxLength = Math.min(MAX_NGRAM, tokens.length - i)
  for (let length = maxLength; length >= 1; length--) {
    const phrase = tokens.slice(i, i + length).join(' ')
    const relationship = RELATIONSHIP_LOOKUP.get(phrase)
    if (relationship) return { canonical: relationship, category: 'relationship', length }
    const occasion = OCCASION_LOOKUP.get(phrase)
    if (occasion) return { canonical: occasion, category: 'occasion', length }
    const location = CITY_LOOKUP.get(phrase)
    if (location) return { canonical: location, category: 'location', length }
    const interest = INTEREST_LOOKUP.get(phrase)
    if (interest) return { canonical: interest, category: 'interest', length }
  }
  return null
}

// Exact-miss fallback for interests only: singular/plural tolerance so "cats"
// and "cat" both land. No edit-distance guessing — matching common English
// words one edit away from a tag (e.g. "looking" → "cooking") fabricates
// interests, and the curated dictionary needs the recall less than the noise.
function fuzzyInterest (token: string): string | null {
  if (token.length < 4) return null

  const singular = token.replace(/(?:es|s)$/, '')
  if (singular !== token) {
    const canonical = INTEREST_LOOKUP.get(singular)
    if (canonical) return canonical
  }
  // Pluralizing a singular token may only hit a canonical name, never a variant,
  // so "craft" can't resolve to art via the "crafts" variant.
  for (const candidate of [`${token}s`, `${token}es`])
    if (INTEREST_NAMES.has(candidate)) return INTEREST_LOOKUP.get(candidate) ?? null

  return null
}

// Hyphens and apostrophes become spaces to match the dictionary keys, so
// "mother-in-law" and "my dad's" split into the same tokens the lookups use.
function tokenize (raw: string): string[] {
  return raw
    .toLowerCase()
    .replace(/[-'’]/g, ' ')
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
}
